import {DOCUMENT} from '@angular/common';
import {ChangeDetectorRef, Component, OnDestroy, OnInit, Renderer2, inject} from '@angular/core';
import {NavigationEnd, NavigationError, Router, RouterOutlet} from '@angular/router';
import {Store} from '@ngrx/store';
import {TranslateService} from '@ngx-translate/core';
import {LoginResponse, OidcSecurityService} from 'angular-auth-oidc-client';
import {Bind} from 'primeng/bind';
import {Toast} from 'primeng/toast';
import {Subject, takeUntil, tap} from 'rxjs';
import {environment} from 'src/environments/environment';
import {AuthLogoutDirective} from './auth-config/directives/auth-logout.directive';
import {LogoutAfterIdleTimeDirective} from './auth-config/directives/logout-after-idle-time.directive';
import {ELoginStatus} from './auth-config/models/auth-state.model';
import {AuthConfigService} from './auth-config/services/auth-config.service';
import {authenticateSuccess, checkAuth, preAuthForAccountConfirmation} from './auth-config/state/auth.actions';
import {TokenUtils} from './auth-config/utils/token.utils';
import {CookieConsentComponent} from './cookie-consent/components/cookie-consent/cookie-consent.component';
import {StorageKeys} from './core/constants/storage-keys';
import {loadFeatureFlags} from './core/state/feature-flag.actions';
import {PlatformStorage} from './core/tokens/platform-storage.token';
import {selectMaintenanceMode} from './maintenance-mode/state/maintenance-mode.selector';
import {selectIsPageNotFound} from './page-not-found/state/page-not-found.selector';
import {BuildNumberComponent} from './shared/components/build-number/build-number.component';
import {FooterComponent} from './shared/components/footer/footer.component';
import {HeaderComponent} from './shared/components/header/header.component';
import {ERole} from './shared/enums/role.enum';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [
    HeaderComponent,
    AuthLogoutDirective,
    LogoutAfterIdleTimeDirective,
    RouterOutlet,
    FooterComponent,
    BuildNumberComponent,
    CookieConsentComponent,
    Bind,
    Toast,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly document = inject<Document>(DOCUMENT);
  private readonly renderer = inject(Renderer2);
  private readonly translate = inject(TranslateService);
  readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly store = inject(Store);
  private readonly authConfigService = inject(AuthConfigService);
  private readonly router = inject(Router);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly storage = inject(PlatformStorage);

  title = 'genetics-portal-frontend';

  private readonly unsubscribe$ = new Subject<void>();
  isPublicPageRoute = false;
  isStaticPageRoute = false;
  isMaintenanceMode = false;
  isPageNotFound = false;
  timerInHHmmss!: string;
  isAuthenticated = false;
  isAuthorize = false;

  constructor() {
    this.timerInHHmmss = environment.idleLogoutTime;
  }

  ngOnInit(): void {
    this.configLanguagesForTranslations();
    this.checkMaintenanceModeSubscription();
    this.setIsPageNotFound();
    this.checkAuthSubscription();
    this.setRecaptchaScriptTag();
  }

  private setRecaptchaScriptTag() {
    const scriptTag = this.renderer.createElement('script');
    scriptTag.type = 'text/javascript';
    scriptTag.src = `https://www.google.com/recaptcha/api.js?render=${environment.recaptcha.siteKey}&trustedtypes=true`;
    scriptTag.async = true;
    scriptTag.defer = true;
    const head = this.document.getElementsByTagName('head')[0];
    head.appendChild(scriptTag);
  }

  isPublicPage(flag: boolean) {
    this.isPublicPageRoute = flag;
  }

  isStaticPage(flag: boolean) {
    this.isStaticPageRoute = flag;
  }

  showHeaderFooter() {
    return (
      ((this.isAuthenticated && this.isAuthorize) || this.isStaticPageRoute) &&
      !(this.isPublicPageRoute || this.isMaintenanceMode || this.isPageNotFound)
    );
  }

  private checkAuthSubscription(): void {
    this.router.events
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(event => {
          if (
            (event instanceof NavigationEnd || event instanceof NavigationError) &&
            !this.authConfigService.checkPreAuthUrl(event as NavigationEnd) &&
            !this.authConfigService.checkThankYouUrl(event as NavigationEnd)
          ) {
            this.checkAuth(event instanceof NavigationError);
          } else if (event instanceof NavigationEnd && this.authConfigService.checkPreAuthUrl(event)) {
            if (this.authConfigService.checkAccountConfirmationUrl(event)) {
              this.store.dispatch(preAuthForAccountConfirmation());
            } else if (!this.authConfigService.checkThankYouUrl(event)) {
              this.store.dispatch(checkAuth());
            }
          }
        })
      )
      .subscribe();
  }

  private configLanguagesForTranslations(): void {
    const language = this.storage.getItem(StorageKeys.currentLanguage);

    this.translate.setDefaultLang(language?.toString() ?? environment.defaultLanguage);
  }

  private checkAuth(isNavError: boolean = false): void {
    this.oidcSecurityService.checkAuth().subscribe({
      next: (auth: LoginResponse) => {
        this.isAuthenticated = auth.isAuthenticated;
        const userRoles = TokenUtils.getUserRole(auth.accessToken);
        const isAuthorize = [ERole.Doctor, ERole.Manager].some(role => userRoles.includes(role));
        this.isAuthorize = isAuthorize;

        if (auth.isAuthenticated) {
          this.storage.setItem(StorageKeys.accessToken, auth.accessToken);
          this.store.dispatch(loadFeatureFlags());
          this.store.dispatch(
            authenticateSuccess({
              token: auth.accessToken,
              status: ELoginStatus.LoggedIn,
            })
          );
        } else if (isNavError && this.authConfigService.isTokenValid()) {
          this.oidcSecurityService.authorize();
        } else if (!this.authConfigService.isTokenValid()) {
          this.store.dispatch(checkAuth());
        }
      },
      error: () => {
        this.oidcSecurityService.authorize();
      },
    });
  }

  private checkMaintenanceModeSubscription(): void {
    this.store
      .select(selectMaintenanceMode)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(maintenanceModeState => (this.isMaintenanceMode = maintenanceModeState)),
        tap(() => this.changeDetectorRef.detectChanges())
      )
      .subscribe();
  }

  private setIsPageNotFound() {
    this.store
      .select(selectIsPageNotFound)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(isPageNotFound => (this.isPageNotFound = isPageNotFound)),
        tap(() => this.changeDetectorRef.detectChanges())
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
